import { Box, Typography, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateMaintenanceTemplateCache, useDeleteMaintenanceTemplate } from 'src/api/maintenanceTemplate'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import GenericTable from 'src/components/table/GenericTable'
import { MaintenanceTemplateType } from 'src/enums'
import { IMaintenanceTemplate } from 'src/generated-types/maintenanceTemplate'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { ShowDialogProps } from 'src/show-dialog'
import EditMaintenanceTemplateForm from './EditMaintenanceTemplateForm'
import { LocalitiesCell } from './LocalitiesCell'

interface MaintenanceTemplateDialogProps extends ShowDialogProps {
  templates: IMaintenanceTemplate[]
  templatesType: MaintenanceTemplateType
  customerId: EntityId
}

const MaintenanceTemplateTable: React.FC<MaintenanceTemplateDialogProps> = ({
  templates,
  templatesType,
  customerId,
}) => {
  const { t: translate } = useTranslation(['maintenance', 'common'])
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()

  const mutation = useDeleteMaintenanceTemplate(customerId as EntityId)

  const onEdit = async id => {
    showDialog(
      EditMaintenanceTemplateForm,
      {
        componentProps: {
          customerId: customerId,
          type: templatesType,
          id: id,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }

  const onDelete = useCallback(id => {
    showConfirmDialog(
      translate('delete-maintenance-template', { ns: 'maintenance' }),
      translate('delete-maintenance-template-text', { ns: 'maintenance' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      pop => {
        mutation.mutate(id, {
          onSuccess: () => {
            toast.success(translate('delete-maintenance-template-success', { ns: 'maintenance' }))
            invalidateMaintenanceTemplateCache.getEquipmentMaintenanceTemplates(customerId as EntityId)
            invalidateMaintenanceTemplateCache.getLocalityMaintenanceTemplates(customerId as EntityId)
            invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(customerId as EntityId)
            invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId as EntityId)
            invalidateMaintenanceCache.invalidateAllMaintenances(customerId)
            pop()
          },
        })
      },
    )
  }, [])

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'name',
      headerName: translate('template-title', { ns: 'maintenance' }),
      flex: 2,
      renderCell: params => (
        <div>
          <div>
            <DynamicTextTypography variant="body1" text={params.row.name} />
          </div>
          <div>
            <DynamicTextTypography
              variant="body2"
              text={params.row.description}
              sx={{ color: theme.palette.secondaryMain[theme.palette.mode] }}
            />
          </div>
        </div>
      ),
    },
    ...(templatesType === MaintenanceTemplateType.Equipment
      ? [
          {
            field: 'type',
            headerName: translate('equipment-type', { ns: 'maintenance' }),
            flex: 2,
            sortable: false,
            renderCell: params => {
              const componentTypesString = params.row.mainComponentTypeMaintenanceTemplates
                .map(x => x.mainComponentType.name)
                .join(', ')
              const componentTypes = JSON.parse(`[${componentTypesString}]`)
              return (
                <div>
                  <div>
                    <Typography variant="body1">{componentTypes.map(item => item.en).join(', ')}</Typography>
                  </div>
                </div>
              )
            },
          },
          {
            field: 'maintenanceCount',
            headerName: '',
            flex: 1,
            sortable: false,
            renderCell: params => (
              <div>
                <div>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.secondaryLight[theme.palette.mode],
                      borderRadius: '4px',
                      padding: '6px',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.primaryMain[theme.palette.mode] }}>
                      {params.row.maintenances.length === 0
                        ? translate('no-maintenances', { ns: 'maintenance' })
                        : `${params.row.maintenances.length} ${
                            params.row.maintenances.length === 1
                              ? translate('maintenance', { ns: 'maintenance' })
                              : translate('maintenances', { ns: 'maintenance' })
                          }`}
                    </Typography>
                  </Box>
                </div>
              </div>
            ),
          },
        ]
      : []),
    ...(templatesType === MaintenanceTemplateType.Locality
      ? [
          {
            field: 'localities',
            headerName: translate('locality', { ns: 'common' }),
            flex: 1,
            sortable: false,
            renderCell: params => {
              const localities = params.row.localityMaintenanceTemplates.map(x => x.locality.name)
              return <LocalitiesCell localities={localities} />
            },
          },
        ]
      : []),
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 10,
        getActions: ({ id }) => [
          <GridActionsCellItem
            key={id}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onEdit(id)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('delete', { ns: 'common' })}
            icon={<MdDelete size={24} />}
            onClick={() => onDelete(id)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [translate],
  )

  const isScrollable = (templates?.length ?? 0) > 10

  return (
    <GenericTable
      key={isScrollable ? 'scrollable' : 'auto-height'}
      id={'maintenance-templates'}
      data={templates}
      columns={columns}
      defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
      enablePagination={false}
      hideDefaultFooterPagination={true}
      getRowClassName={params => (params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd')}
      getRowHeight={() => 44}
      autoHeight={!isScrollable}
      height={isScrollable ? '474px' : undefined}
    />
  )
}

export default MaintenanceTemplateTable
